<ng-container *transloco="let t; prefix: 'overtimeManagement'">
<div class="driver-details d-flex gap-3 flex-column">
  @if (isOvertimeDetailsLoading && !changePageLoading) {
    <div class="spinner-overlay">
      <div class="d-flex align-items-center justify-content-center spinner-border"></div>
    </div>
  }
  @if (!isOvertimeDetailsLoading && this.selectedDriver) {
    <div class="driver-details__body">
      <!-- Header -->
      <div class="driver-details__header">
        <div class="driver-details__employee-info d-flex flex-column gap-3">
          <span class="driver-details__employee-name">{{ this.selectedDriver.firstName + ' ' +
            this.selectedDriver.lastName }}</span>
          <span class="driver-details__employee-company">{{ this.selectedDriver.accountName }}</span>
        </div>
        <div class="driver-details__amount">
          @if (overtimeStatusLoading) {
            <span>
              <span class="spinner-border"></span>
            </span>
          } @else {
            <span>
              {{ formatCost(this.driverOvertimeTotalCost.totalCost) }}
            </span>
          }
        </div>
      </div>

      <!-- Decision Section -->
      <div class="driver-details__decision-section my-2">
        <span class="driver-details__decision-title">{{ t('details.totalOvertimeFor', { name: this.selectedDriver.firstName + ' ' + this.selectedDriver.lastName }) }}</span>
        @if (this.selectedDriver.hasPendingOvertime) {
          <button class="driver-details__decision-btn">
            <span class="driver-details__decision-text">{{ t('details.requiresDecision') }}</span>
          </button>
        }
      </div>

      <!-- Main Table -->
      <div class="driver-details__table">
        <table class="table">
          <thead>
            <tr class="driver-details__table-header">
              @for (header of detailsTableHeaders; track header.key) {
                <th class="driver-details__table-header-cell col-2">
                  <div class="d-flex align-items-center" (click)="sortData(header.key)">
                    {{ header.label }}
                    @if (header.sortable && sortColumn !== header.key) {
                      <i class="ph-duotone ph-arrows-down-up"></i>
                    }
                    @if (header.sortable && sortColumn === header.key && sortDirection === 'asc') {
                      <i class="ph-duotone ph-arrow-up"></i>
                    }
                    @if (header.sortable && sortColumn === header.key && sortDirection === 'desc') {
                      <i class="ph-duotone ph-arrow-down"></i>
                    }
                  </div>
                </th>
              }
            </tr>
          </thead>
        </table>
        <!-- Dynamic Table Rows -->
        @if (changePageLoading) {
          <div class="d-flex justify-content-center align-items-center mt-5">
            <span class="spinner-border"></span>
          </div>
        } @else {
          <span>
            @for (entry of sortedOvertimeEntries; track entry.id; let i = $index) {
              <div>
                <table class="table">
                  <tbody>
                    <tr class="driver-details__table-row" (click)="toggleRowExpansion(i)">
                      <td class="driver-details__table-cell col-2"><span>{{ entry.createdAt | date: 'EEE M/d/yy'
                          }}</span></td>
                      <td class="driver-details__table-cell col-2"><span>{{ entry.overtimeTrips.length }}</span>
                      </td>
                      <td class="driver-details__table-cell col-2"><span>{{ entry.duration }}</span>
                      </td>
                      <td class="driver-details__table-cell col-2"><span class="fw-bold">{{ entry.formattedHours
                          }}</span>
                      </td>
                      <td class="driver-details__table-cell col-2"><span class="fw-bold">{{ formatCost(entry.cost) }}</span></td>
                      <td class="driver-details__table-cell col-2">
                        <div class="d-flex gap-2">
                          @if (entry.status === 3) {
                            <button class="btn-reject"
                              (click)="rejectOvertime(entry.id); $event.stopPropagation()">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                  d="M10.7946 4.22949L7.96598 7.05749L5.13798 4.22949L4.19531 5.17216L7.02331 8.00016L4.19531 10.8282L5.13798 11.7708L7.96598 8.94283L10.7946 11.7708L11.7373 10.8282L8.90931 8.00016L11.7373 5.17216L10.7946 4.22949Z"
                                  fill="#4D4749" />
                              </svg>
                            </button>

                            <button class="btn-approve w-100"
                              (click)="approveOvertime(entry.id); $event.stopPropagation()">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                  d="M6.66925 10.391L4.47392 8.19564L3.53125 9.1383L6.66925 12.2763L13.1406 5.80497L12.1979 4.8623L6.66925 10.391Z"
                                  fill="#EE2737" />
                              </svg>
                              {{ t('details.approve') }}</button>
                          }

                          <!-- Approved Button -->
                          @if (entry.status === 1) {
                            <button class="btn-approved"
                              (click)="revertDecision(entry.id); $event.stopPropagation()">
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <path
                                  d="M1.16678 13.0001C1.22078 13.0001 1.27478 12.9934 1.32811 12.9801L3.99478 12.3134C4.11211 12.2841 4.21945 12.2234 4.30478 12.1381L12.5001 3.94274C12.7521 3.69074 12.8908 3.35607 12.8908 3.00007C12.8908 2.64407 12.7521 2.3094 12.5001 2.0574L11.4428 1.00007C10.9388 0.49607 10.0614 0.49607 9.55745 1.00007L1.36211 9.1954C1.27678 9.28074 1.21611 9.38807 1.18678 9.50474L0.520112 12.1714C0.462779 12.3987 0.530112 12.6387 0.695446 12.8047C0.821446 12.9314 0.992112 13.0001 1.16678 13.0001ZM10.5001 1.94274L11.5574 3.00007L10.5001 4.0574L9.44278 3.00007L10.5001 1.94274ZM2.43545 10.0074L8.50011 3.94274L9.55745 5.00007L3.49211 11.0647L2.08278 11.4167L2.43545 10.0074Z"
                                  fill="#EE2737" />
                              </svg>
                              {{ t('details.approved') }}
                            </button>
                          }

                          <!-- Rejected Button -->
                          @if (entry.status === 0) {
                            <button class="btn-rejected"
                              (click)="revertDecision(entry.id); $event.stopPropagation()">
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                <path
                                  d="M1.16678 13.0001C1.22078 13.0001 1.27478 12.9934 1.32811 12.9801L3.99478 12.3134C4.11211 12.2841 4.21945 12.2234 4.30478 12.1381L12.5001 3.94274C12.7521 3.69074 12.8908 3.35607 12.8908 3.00007C12.8908 2.64407 12.7521 2.3094 12.5001 2.0574L11.4428 1.00007C10.9388 0.49607 10.0614 0.49607 9.55745 1.00007L1.36211 9.1954C1.27678 9.28074 1.21611 9.38807 1.18678 9.50474L0.520112 12.1714C0.462779 12.3987 0.530112 12.6387 0.695446 12.8047C0.821446 12.9314 0.992112 13.0001 1.16678 13.0001ZM10.5001 1.94274L11.5574 3.00007L10.5001 4.0574L9.44278 3.00007L10.5001 1.94274ZM2.43545 10.0074L8.50011 3.94274L9.55745 5.00007L3.49211 11.0647L2.08278 11.4167L2.43545 10.0074Z"
                                  fill="#4D4749" />
                              </svg>
                              {{ t('details.rejected') }}
                            </button>
                          }
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <!-- Expandable Details Row -->
                @if (expandedRowIndex === i) {
                  <table class="driver-details__table-details table">
                    <thead>
                      <tr class="driver-details__table-header">
                        @for (header of nestedTableHeaders; track $index) {
                          <th class="driver-details__table-header-cell">
                            {{ header }}
                          </th>
                        }
                      </tr>
                    </thead>
                    <tbody>
                      @for (trip of entry.overtimeTrips; track $index) {
                        <tr class="driver-details__table-row">
                          <td class="driver-details__table-cell"><span>{{ trip.assetName }}</span></td>
                          <td class="driver-details__table-cell"><span>{{ trip.tripStartLocation }}</span></td>
                          <td class="driver-details__table-cell"><span>{{ trip.tripEndLocation }}</span></td>
                          <td class="driver-details__table-cell"><span>{{ trip.distance + 'km'}}</span></td>
                          <td class="driver-details__table-cell"><span>{{ trip.fuelConsumption + 'ℓ' }}</span></td>
                          <td class="driver-details__table-cell"><span>{{ trip.duration + 'hrs' }}</span></td>
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              </div>
            }
          </span>
        }

        <table class="table table-footer">
          <tbody>
            <tr class="driver-details__table-row">
              <td class="py-3 px-5 driver-details__table-cell" colspan="6">
                <div class="d-flex flex-column gap-3">
                  @if (overtimeStatusLoading) {
                    <span class="d-flex justify-content-end">
                      <span class="spinner-border"></span>
                    </span>
                  } @else {
                    <div class="d-flex justify-content-end">
                      <span class="fw-bold text-end pe-3 driver-details__table-cell--total">{{ t('details.totalHours') }}</span> <span class="fw-bold text-end ps-3  driver-details__table-cell--total-hours">{{ this.driverOvertimeTotalCost.overtimeHours }}</span>
                    </div>
                    <div class="d-flex justify-content-end">
                      <span class="fw-bold text-end pe-3 driver-details__table-cell--highlight">{{ t('details.totalCosts') }}</span> <span class="fw-bold text-end ps-3 driver-details__table-cell--highlight-value">{{ formatCost(this.driverOvertimeTotalCost.totalCost) }}</span>
                    </div>
                  }
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <table class="table table-footer">
          <tfoot>
            <tr>
              <td class="p-3"><span class="driver-details__results">{{ 'common.showingOutOf' | transloco: { current: currentItems, total: totalCount } }}</span></td>
              <td colspan="3">
                <div class="driver-details__pagination">
                  <app-pagination [totalPages]="totalPages" [currentPage]="currentPage"
                    (pageChange)="goToPage($event)"></app-pagination>
                </div>
              </td>
              <td class="p-3">
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  }
</div>
</ng-container>
